import { NgTemplateOutlet } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import { ChangeDetectionStrategy, Component, input, viewChild } from '@angular/core';

import { TranslatePipe } from '@recruitee/i18n';
import {
  ButtonComponent,
  ConnectedOverlayDirective,
  IconComponent,
  InterviewEvaluationRatingComponent,
  OverlayOriginDirective,
  OverlayPosition,
  OverlayPositionPairs,
} from '@recruitee/user-interface';

/**
 * Component for evaluating a candidate with a button that toggles an overlay.
 * The button displays the evaluation state and opens a dropdown for new or existing evaluations.
 */
@Component({
  selector: 'rt-evaluate-candidate-button',
  template: `
    <rt-button
      #overlayOrigin="rtOverlayOrigin"
      variant="plain"
      [pressed]="overlay.open"
      [size]="$any(rating() ? 'small' : 'medium')"
      (click)="overlay.toggleOverlay(true)"
      [class]="rating()"
      rt-overlay-origin
    >
      @if (!rating()) {
        <rt-icon name="thumbs-up-down" class="not-evaluated-button"></rt-icon>
        {{ 'app.interview.not_evaluated' | translate }}
      } @else {
        {{ 'app.interview.you_evaluated' | translate }}
        <rt-interview-evaluation-rating
          [tooltip]="false"
          [colorize]="false"
          [rating]="rating()"
          [size]="'small'"
          margin-left-small-xx
        >
        </rt-interview-evaluation-rating>
      }
    </rt-button>
    <ng-template
      #overlay="rtConnectedOverlay"
      [origin]="overlayOrigin"
      [positions]="[overlayPosition]"
      (backdropClick)="overlay.toggleOverlay(!overlay.open)"
      rt-connected-overlay
      hasBackdrop
    >
      <ng-container
        *ngTemplateOutlet="this.rating() ? editEvaluationDropdownTpl() : newEvaluationDropdownTpl()"
      ></ng-container>
    </ng-template>
  `,
  styleUrls: ['evaluate-candidate-button.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    IconComponent,
    TranslatePipe,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    NgTemplateOutlet,
    InterviewEvaluationRatingComponent,
  ],
})
export class EvaluateCandidateButtonComponent {
  public readonly overlayPosition = OverlayPositionPairs[OverlayPosition.BottomLeft];

  /**
   * The current rating of the candidate. It is a required input.
   */
  public rating = input.required<string | null>();

  /**
   * Template for the dropdown when creating a new evaluation. It is a required input.
   */
  public newEvaluationDropdownTpl = input.required<TemplateRef<any> | null>();

  /**
   * Template for the dropdown when editing an existing evaluation. It is a required input.
   */
  public editEvaluationDropdownTpl = input.required<TemplateRef<any> | null>();

  public overlay = viewChild(ConnectedOverlayDirective);
}
